import React, { memo, useCallback, useEffect, useMemo, useState } from "react"
import QuickWidgetDialog from "../../../quickWidgetDialog/quickWidgetDialog"
import { useSelector } from "react-redux"
import { Box, Stack, Typography } from "@mui/material"
import { QuickWidgetTypes } from "../../../../reducer/user.reducer"
import { fetchQuickTrades } from "../../../../services/apis/quickTrades"
import { selectQuickTrades } from "../../../../selectors/quickTrades"
import { ExtraSmallButton, NonInteractiveTooltip } from "../../../custom"
import { QuickTradeProto } from "../../../../proto/generated/quick-trade"
import { strategyParser } from "../../../strategyTemplate/StrategyParser"
import { OptionChainViewKey } from "../../../../assets/constants"
import AddQuickTrade from "./AddQuickTrade"
import { selectPrimaryTradingAccount, selectTradingAccounts } from "../../../../selectors/user"
import { PlaceOrModifyOrdersRequest, PlaceOrModifyOrdersResponse, TradingAccountStatus } from "../../../../proto/generated/trading-bridge"
import { setOrderDetails, updateOrderHistory } from "../../../../reducer/orders.reducer"
import { checkForDuplicateOrders, isDatePassed } from "../../../../util/tradepanel"
import { Order, OrderStatus, OrderType, Product, TransactionType, transactionTypeFromJSON, Validity, Broker_Variety, Broker_OrderType } from "../../../../proto/generated/order"
import { useDispatch } from "react-redux"
import { v4 as uuid } from "uuid"
import { getExchangeAndCodeFromKey, getSecurityAsync, getSecurityKey } from "../../../../util/security"
import { getOrderPriceFromQuote, getOrderPricesFromQuote } from "../../../../util/quicktradewidget"
import { computeOrderQtyFromTemplateParams, effectiveLotSize, isFoSecurity } from "../../../../util/quickTradeQuantity"
import { resolveQuickTradeProduct } from "../../../../util/openQuickTrade"
import { appendTemplateSlTargetLegs } from "../../../../util/quickTradeTemplateSlTarget"
import {
    applyTemplateConditionalToSingleLegOrders,
    appendTemplateConditionalLegs,
} from "../../../../util/quickTradeTemplateConditional"
import AddIcon from "../../../../assets/icons/AddIcon"
import { setOpenQuickTradeSettingsDialog } from "../../../../reducer/quickTrades.reducer"
import { placeOrModifyOrder } from "../../../../services/apis/orders"
import commonActions from "../../../../actions/common.actions"
import { selectPrimarySecurity } from "../../../../selectors/home"
import { StrategyTemplateProto } from "../../../../proto/generated/strategy"
import QuickTradeTooltip from "./QuickTradeTooltip"
import Loading from "../../Loading"
import { Security, SecurityCategory_CategoryCode } from "../../../../proto/generated/security"
import { isTradeableSecurity } from "../../../../util/cashInstrument"
import ROUTES from "../../../../assets/routes"
import { matchPath, useLocation } from "react-router-dom"
import { addOrderAlertMessage } from "../../../../util/orders"

const autoSecurityDisabledRoutes = [ROUTES.reports, ROUTES.fiiDii, ROUTES.screener]

function applyQuickTradePlaceResponseToRedux(
    dispatch: ReturnType<typeof useDispatch>,
    requestOrders: Order[],
    response: PlaceOrModifyOrdersResponse
): void {
    const updates: Order[] = []
    ;(response.orderResponse || []).forEach((orderResponse, i) => {
        const { successful, errorMessage, order } = orderResponse
        if (!successful) {
            addOrderAlertMessage({
                ...(requestOrders[i] ?? requestOrders[0]),
                status: OrderStatus.FAILED,
                message: errorMessage || 'Unable to place order',
            })
            return
        }
        if (order) {
            updates.push(order)
            addOrderAlertMessage({
                ...order,
                message: 'Order placed successfully',
            })
        }
    })
    if (updates.length) {
        dispatch(updateOrderHistory({ updates }))
    }
}

const QuickTradeWidget: React.FC = () => {
    const dispatch = useDispatch()
    const quickTrades = useSelector(selectQuickTrades)
    const [disableButtons, setDisableButtons] = useState(false)
    const tradingAccounts = useSelector(selectTradingAccounts)
    const location = useLocation()
    const primaryTradingAccount = useSelector(selectPrimaryTradingAccount)
    const { key } = useSelector(selectPrimarySecurity)
    const [loading, setLoading] = useState(true)
    const { primary } = useSelector(selectPrimarySecurity)
    const [securityCache, setSecurityCache] = useState<Record<string, Security | null>>({})
    useEffect(() => {
        (async () => {
            if (quickTrades.length === 0) {
                setLoading(true)
                await fetchQuickTrades()
                setLoading(false)
            } else {
                setLoading(false)
            }
        })()
    }, [])

    const secKeysToLoad = useMemo(() => {
        const keys = new Set<string>()
        quickTrades.forEach((qt) => {
            const computed = qt?.security?.code ? qt?.security : primary
            if (computed?.exchange != null && computed?.code) {
                keys.add(getSecurityKey(computed.exchange, computed.code))
            }
        })
        if (primary?.exchange != null && primary?.code) {
            keys.add(getSecurityKey(primary.exchange, primary.code))
        }
        return Array.from(keys)
    }, [quickTrades, primary])

    useEffect(() => {
        if (!secKeysToLoad.length) return
        let cancelled = false
        Promise.all(secKeysToLoad.map((k) => getSecurityAsync(k))).then((secs) => {
            if (cancelled) return
            const next: Record<string, Security | null> = {}
            secKeysToLoad.forEach((k, i) => { next[k] = secs[i] ?? null })
            setSecurityCache((prev) => ({ ...prev, ...next }))
        })
        return () => { cancelled = true }
    }, [secKeysToLoad])

    const checkForButtonDisable = useCallback((quickTrade: QuickTradeProto) => {
        const autoSecurity = quickTrade?.security?.code ? false : true
        if (autoSecurity) {
            if (autoSecurityDisabledRoutes.some(route=>matchPath(route, location.pathname))) {
                return true
            }
        }
        const computedSecurity = quickTrade?.security?.code ? quickTrade?.security : primary
        const secKey = getSecurityKey(computedSecurity?.exchange??0, computedSecurity?.code??'')
        const security = secKey ? securityCache[secKey] : undefined
        if(security?.expiry && isDatePassed(security?.expiry?.expiry)) {
            return true
        }
        if(quickTrade?.templateOrderExecutionParameters?.transactionType) {
            if([SecurityCategory_CategoryCode.FUTURES, SecurityCategory_CategoryCode.OPTIONS].includes(security?.categoryCode) || isTradeableSecurity(security)) {
                return false
            }
            return true
        } else if(quickTrade?.templateOrderExecutionParameters?.strategyLegs?.strategyTemplateLegs.length>0) {
            if(security?.hasFo) {
                return false
            }
            return true
        }
        return true
    }, [primary, location.pathname, securityCache])

    const handleQuickTradeAction = async (quickTrade: QuickTradeProto) => {
        const underlyingSecKey = quickTrade.security.code ? getSecurityKey(quickTrade.security?.exchange ?? 0, quickTrade.security?.code ?? '') : key
        const mainSecurity = await getSecurityAsync(underlyingSecKey)
        const template: StrategyTemplateProto = {
            strategyTemplateLegs: quickTrade.templateOrderExecutionParameters.strategyLegs,
            name: ''
        } as StrategyTemplateProto
        if (!template || !underlyingSecKey) {
            console.error("Template not found for quick trade action")
            return
        }
        const [exchange, code] = getExchangeAndCodeFromKey(underlyingSecKey)
        let orderData: any = []
        if(!quickTrade.templateOrderExecutionParameters.transactionType) {
            orderData = await strategyParser(template, { code, exchange }, [], OptionChainViewKey.LIVE, true)
        }

        //Execute the quick trade action

        if ((primaryTradingAccount?.status !== TradingAccountStatus.ACTIVE)) {
            return
        } else if (quickTrade?.templateOrderExecutionParameters?.strategyLegs?.strategyTemplateLegs?.length>0 && checkForDuplicateOrders(orderData.orders)) {
            return
        } else if (quickTrade?.templateOrderExecutionParameters?.transactionType) { // buy sell single leg
            const brokerOrderType = quickTrade?.templateOrderExecutionParameters?.brokerOrderType
            const templateParams = quickTrade.templateOrderExecutionParameters!
            const quoteBaseDisplay = Number(await getOrderPriceFromQuote(
                mainSecurity,
                templateParams.transactionType!,
                templateParams.quoteType,
                templateParams.quoteValue
            ))

            const lotSize = mainSecurity.lotSize ?? 1
            let quantity = computeOrderQtyFromTemplateParams(
                {
                    lots: templateParams.lots,
                    quantity: templateParams.quantity,
                    amount: templateParams.amount,
                },
                lotSize,
                quoteBaseDisplay
            )
            const minQty = isFoSecurity(mainSecurity) ? effectiveLotSize(lotSize) : 1
            if (quantity < minQty) {
                const minLabel = isFoSecurity(mainSecurity)
                    ? `1 lot (${effectiveLotSize(lotSize)} units)`
                    : '1 unit'
                commonActions.setApiError(dispatch, {
                    error: true,
                    message: `Minimum quantity required: ${minLabel}`,
                    errorType: 'error'
                })
                addOrderAlertMessage({
                    security: {
                        exchange: mainSecurity.exchange,
                        code: mainSecurity.code
                    },
                    status: OrderStatus.REJECTED,
                    message: `Minimum quantity required: ${minLabel}`
                } as Order)
                return
            }
            const product = resolveQuickTradeProduct(mainSecurity)
            const isMarketOrder = brokerOrderType === Broker_OrderType.MARKET_ORDER || brokerOrderType === Broker_OrderType.STOPLOSS_MARKET_ORDER

            const batchId = uuid()
            const strategyGroup = `${mainSecurity.symbol} - ${quickTrade.actionName}`
            const request: PlaceOrModifyOrdersRequest = {
                tradingAccountId: primaryTradingAccount.accountId??'',
                buyBeforeSell: false,
                order: [{
                    security: {
                        exchange: mainSecurity.exchange,
                        code: mainSecurity.code
                    },
                    transactionType: quickTrade?.templateOrderExecutionParameters?.transactionType,
                    orderType: OrderType.NORMAL,
                    brokerOrderType,
                    quantity: Number(quantity),
                    product,
                    price: isMarketOrder ? 0 : Math.round(quoteBaseDisplay * (mainSecurity?.priceMultiplier ?? 100)),
                    triggerPrice: 0,
                    brokerVariety: Broker_Variety.REGULAR,
                    validity: Validity.DAY,
                    clientOrderId: uuid(),
                    orderDate: Date.now(),
                    isBasket: false,
                    batchId,
                    strategyGroup,
                    immediateExecutePaperOrder: false
                }]
            }
            appendTemplateSlTargetLegs(request.order, {
                templateParams,
                mainSecurity,
                entryPriceDisplay: quoteBaseDisplay,
                quantity: Number(quantity),
                product,
                batchId,
                strategyGroup,
            })
            applyTemplateConditionalToSingleLegOrders(request.order, {
                templateParams,
                mainSecurity,
                batchId,
                strategyGroup,
                primaryQuantity: Number(quantity),
                primaryTransactionType: templateParams.transactionType!,
                primaryProduct: product,
            })
            try {
                const response = await placeOrModifyOrder(request)
                applyQuickTradePlaceResponseToRedux(dispatch, request.order, response)
            } catch (error: any) {
                addOrderAlertMessage({
                    ...request.order[0],
                    status: OrderStatus.FAILED,
                    message: error.message || 'Unable to place order'
                })
            }
        } else { // buy sell basket 
            setDisableButtons(true)
            const updatedBasketId = uuid();
            const batchId = uuid()
            const strategyGroup = `${mainSecurity.symbol} - ${quickTrade.actionName}`
            const orderPrices = await getOrderPricesFromQuote(orderData.orders, orderData.orderPrices, quickTrade.templateOrderExecutionParameters.quoteType, quickTrade.templateOrderExecutionParameters.quoteValue)
            let quantityMultiplier = quickTrade?.templateOrderExecutionParameters?.multiplier
            if (quantityMultiplier == null && orderData.orders.length === 1 && orderData.orders[0].type === 'buy') {
                if (quickTrade?.templateOrderExecutionParameters.amount) {
                    const oneLotQunatity = (orderData.orders[0].quantity) * (orderData.orders[0].security?.lotSize ?? 0)
                    const price = Math.round(Number(orderPrices[0].price)) * oneLotQunatity
                    quantityMultiplier = Math.floor((quickTrade?.templateOrderExecutionParameters?.amount ?? 0) / price)
                    if (quantityMultiplier === 0) {
                        commonActions.setApiError(dispatch, {
                            error: true,
                            message: `Min. Amount required: ${price}, Your amount: ${quickTrade?.templateOrderExecutionParameters?.amount ?? 0}`,
                            errorType: 'error'
                        })
                        addOrderAlertMessage({
                            security: {
                                exchange: mainSecurity.exchange,
                                code: mainSecurity.code
                            },
                            status: OrderStatus.REJECTED,
                            message: `Min. Amount required: ${price}, Your amount: ${quickTrade?.templateOrderExecutionParameters?.amount ?? 0}`
                        } as Order)
                        setDisableButtons(false)
                        return
                    }
                } else {
                    quantityMultiplier = 1
                }
            }
            const orderDate = Date.now();
            const orderDetailsArray = await Promise.all(
                orderData.orders.map(async (order, i) => {
                    if (!order.security) {
                        console.error('Security is not defined for order', order)
                        throw new Error('Security is not defined for order');
                    }
                    const security = await getSecurityAsync(getSecurityKey(order.security.exchange ?? 0, order.security.code ?? ''));
                    if (!security) throw new Error('Security not present');
                    const newUuid = order.uuid
                    const orderDetails: Order = {
                        security: {
                            code: security?.code,
                            exchange: security?.exchange
                        },
                        transactionType: transactionTypeFromJSON(order.type.toUpperCase()),
                        orderType: OrderType.NORMAL,
                        brokerOrderType: quickTrade.templateOrderExecutionParameters?.brokerOrderType,
                        quantity: (order.quantity) * (order.security?.lotSize ?? 0) * (quantityMultiplier ?? 1),
                        product: resolveQuickTradeProduct(security),
                        price: quickTrade.templateOrderExecutionParameters?.brokerOrderType === Broker_OrderType.MARKET_ORDER ? 0 : Math.round(Number(orderPrices[i].price) * (mainSecurity?.priceMultiplier ?? 100)),
                        triggerPrice: 0,
                        brokerVariety: Broker_Variety.REGULAR,
                        validity: Validity.DAY,
                        clientOrderId: newUuid,
                        orderDate,
                        isBasket: true,
                        basketId: updatedBasketId,
                        batchId,
                        strategyGroup,
                        immediateExecutePaperOrder: false
                    }
                    dispatch(setOrderDetails({ index: i, orderDetails }))
                    return orderDetails;
                })
            )
            const templateParams = quickTrade.templateOrderExecutionParameters!
            const request: PlaceOrModifyOrdersRequest = {
                tradingAccountId: primaryTradingAccount.accountId,
                buyBeforeSell: true,
                order: orderDetailsArray
            }
            if (
                (templateParams.addStopLoss && templateParams.stopLoss != null) ||
                (templateParams.addTarget && templateParams.target != null)
            ) {
                appendTemplateSlTargetLegs(request.order, {
                    templateParams,
                    mainSecurity,
                    quantity: 1,
                    product: resolveQuickTradeProduct(mainSecurity),
                    batchId,
                    basketId: updatedBasketId,
                    strategyGroup,
                })
            }
            appendTemplateConditionalLegs(request.order, {
                templateParams,
                mainSecurity,
                batchId,
                basketId: updatedBasketId,
                strategyGroup,
            })
            try {
                const response = await placeOrModifyOrder(request)
                applyQuickTradePlaceResponseToRedux(dispatch, request.order, response)
            } catch (error: any) {
                addOrderAlertMessage({
                    ...request.order[0],
                    status: OrderStatus.FAILED,
                    message: error.message || 'Unable to place order'
                })
            }
            setDisableButtons(false)
        }
    }

    return (
        <QuickWidgetDialog widgetType={QuickWidgetTypes.QUICK_TRADE} title="Quick Trade">
            <Box sx={{ display: 'flex', gap: '6px', height: '100%', flexWrap: 'wrap', maxHeight: 'max-content', background: 'none', overflowY: 'auto', p: 0.75 }}>
                {
                    loading ?
                        <Loading size={'26px'} /> :
                        <>
                            {
                                quickTrades.length === 0 ? null :
                                    <Stack direction={'row'} gap={0.75} flexWrap={'wrap'}>
                                        {
                                            quickTrades.map((q) => (
                                                <NonInteractiveTooltip
                                                    title={<QuickTradeTooltip quickTrade={q} />}
                                                    sx={{
                                                        '& .MuiTooltip-tooltip': {
                                                            width: 'min-content',
                                                            maxWidth: 'none',
                                                            bgcolor: 'background.paper',
                                                            color: 'text.primary',
                                                            border: (theme) => `1px solid ${theme.palette.divider}`,
                                                            boxShadow: (theme) => theme.shadows[theme.palette.mode === 'dark' ? 8 : 3],
                                                        },
                                                    }}
                                                    arrow
                                                >
                                                    <ExtraSmallButton
                                                        sx={{
                                                            maxWidth: '180px',
                                                            height: 28,
                                                            minHeight: 28,
                                                            fontSize: '12px',
                                                            px: 1,
                                                            py: 0,
                                                            borderColor: theme => theme.palette[q.templateOrderExecutionParameters.transactionType ? (q.templateOrderExecutionParameters.transactionType === TransactionType.BUY ? 'buy' : 'sell') : 'primary'].main,
                                                        }}
                                                        variant="outlined"
                                                        color={q.templateOrderExecutionParameters.transactionType ? (q.templateOrderExecutionParameters.transactionType === TransactionType.BUY ? 'buy' : 'sell') : 'primary'}
                                                        key={q.id + '-action-btn'}
                                                        disabled={disableButtons || checkForButtonDisable(q)}
                                                        onClick={() => handleQuickTradeAction(q)}
                                                    >
                                                        <div style={{
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                            whiteSpace: "nowrap"
                                                        }}>
                                                            {q.actionName}
                                                        </div>
                                                    </ExtraSmallButton>
                                                </NonInteractiveTooltip>
                                            ))
                                        }
                                    </Stack>
                            }
                            {
                                quickTrades.length === 0 ?
                                    <Box sx={{ height: '100%', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                        <ExtraSmallButton
                                            variant="outlined"
                                            disabled={disableButtons}
                                            sx={{ height: 28, minHeight: 28, fontSize: '12px', px: 1 }}
                                            onClick={() => {
                                            dispatch(setOpenQuickTradeSettingsDialog({
                                                settingsDialogOpen: true,
                                                dialogMode: 'addOrEdit',
                                                editTemplateId: undefined
                                            }))
                                        }}>
                                            <AddIcon sx={{ fontSize: 14 }} />
                                            {
                                                ' Add new action'
                                            }
                                        </ExtraSmallButton>
                                    </Box> :
                                    <NonInteractiveTooltip title={'Add new action'} arrow>
                                        <ExtraSmallButton
                                            variant="outlined"
                                            disabled={disableButtons}
                                            sx={{ height: 28, minWidth: 28, minHeight: 28, px: 0.75 }}
                                            onClick={() => {
                                            dispatch(setOpenQuickTradeSettingsDialog({
                                                settingsDialogOpen: true,
                                                dialogMode: 'addOrEdit',
                                                editTemplateId: undefined
                                            }))
                                        }}>
                                            <AddIcon sx={{ fontSize: 14 }} />
                                        </ExtraSmallButton>
                                    </NonInteractiveTooltip>
                            }
                        </>
                }
            </Box>
            <AddQuickTrade />
        </QuickWidgetDialog>
    )
}

export default memo(QuickTradeWidget)